import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

const SOURCE_SELECTOR = 'template[data-r-menu-previews]';
const DESKTOP_HOVER = '(min-width: 990px) and (hover: hover) and (pointer: fine)';

/**
 * Reduce a link to a match key.
 *
 * Both sides are already resolved paths — Liquid resolves a block's `url` setting the
 * same way it renders a menu href — so this only absorbs the trailing-slash and
 * query-string variation a merchant's picked URL can carry against the menu's own. A
 * mismatch here would not throw; the override would just silently never apply.
 *
 * @param {string | null | undefined} raw
 */
function previewKey(raw) {
  const path = (raw ?? '').trim().split('?')[0].split('#')[0].toLowerCase();
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/** @type {Promise<DocumentFragment | null> | null} */
let source = null;

/* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§7 */
function loadSource() {
  if (source) return source;

  const inline = document.querySelector(`header-menu ${SOURCE_SELECTOR}`);
  source = Promise.resolve(inline instanceof HTMLTemplateElement ? inline.content : null);

  return source;
}

/* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§1 */
class MenuPreview extends Component {
  /** @type {HTMLElement | null} */
  #grid = null;

  /** @type {HTMLElement | null} */
  #item = null;

  /** @type {HTMLAnchorElement | null} */
  #trigger = null;

  /** @type {Map<string, HTMLElement>} */
  #layers = new Map();

  /** @type {HTMLElement | null} */
  #resting = null;

  /** @type {HTMLElement | null} */
  #active = null;

  #backfilling = false;

  /** @type {Promise<boolean> | null} */
  #adopted = null;

  connectedCallback() {
    super.connectedCallback();

    this.#grid = this.closest('.mega-menu__grid');
    this.#item = this.closest('.menu-list__list-item');
    this.#trigger = this.#item?.querySelector('.menu-list__link') ?? null;

    if (!this.#grid) {
      this.remove();
      return;
    }

    this.#grid.addEventListener('pointerover', this.#onIntent);
    this.#grid.addEventListener('focusin', this.#onIntent);
    this.#item?.addEventListener('pointerenter', this.#onEnter);
    this.#item?.addEventListener('pointerleave', this.#onLeave);
    this.#trigger?.addEventListener('focus', this.#onEnter);

    // The source is in the page. On a hover-capable desktop adopt at connect so the
    // first hover is instant; elsewhere the rail is hidden (below 990px) or has no
    // hover to serve, so adoption waits for an intent that may never come.
    if (matchMedia(DESKTOP_HOVER).matches) this.#ensureAdopted();
  }

  /* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§3 */
  updatedCallback() {
    super.updatedCallback();
    if (!this.#grid || !this.#adopted) return;

    if (!this.#scope()) {
      this.remove();
      return;
    }

    this.#show(this.#active?.isConnected ? this.#active : this.#resting);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#cancelBackfill();
    this.#grid?.removeEventListener('pointerover', this.#onIntent);
    this.#grid?.removeEventListener('focusin', this.#onIntent);
    this.#item?.removeEventListener('pointerenter', this.#onEnter);
    this.#item?.removeEventListener('pointerleave', this.#onLeave);
    this.#trigger?.removeEventListener('focus', this.#onEnter);
    this.#grid = this.#item = this.#trigger = null;
    this.#layers.clear();
  }

  /* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§9 */
  #ensureAdopted() {
    if (this.#adopted) return this.#adopted;

    this.#adopted = loadSource().then((fragment) => {
      if (!this.isConnected) return false;

      const stage = this.querySelector('.r-menu-preview__stage');
      if (!fragment || !stage) {
        // Nothing to show — a store with no images at all. Hand the panel back its
        // full-width columns rather than leave an empty frame.
        this.remove();
        return false;
      }

      stage.querySelector('[data-r-preview-placeholder]')?.remove();
      for (const layer of fragment.querySelectorAll('.r-menu-preview__layer')) {
        stage.append(layer.cloneNode(true));
      }

      if (!this.#scope()) {
        this.remove();
        return false;
      }

      return true;
    });

    return this.#adopted;
  }

  /**
   * Index every layer under each link URL it answers for, dropping what this panel cannot
   * reach, and pick the one that rests.
   *
   * The first claim on a key wins, and merchant overrides are emitted before the derived
   * layers, so overrides take precedence without needing a marker.
   *
   * Resting is the layer answering for the menu's own top-level link, else the first
   * surviving layer.
   *
   * @returns {boolean} whether any layer survived
   */
  #scope() {
    this.#layers.clear();

    const reachable = new Set();
    for (const link of this.#grid?.querySelectorAll('a[href]') ?? []) {
      reachable.add(previewKey(link.getAttribute('href')));
    }
    // The trigger sits outside the grid, but the panel still has a layer for it.
    const triggerKey = previewKey(this.#trigger?.getAttribute('href'));
    if (triggerKey) reachable.add(triggerKey);

    let first = null;

    for (const layer of [...this.querySelectorAll('.r-menu-preview__layer')]) {
      let claimed = 0;

      for (const raw of (layer.getAttribute('data-r-preview-link') ?? '').split(/\s+/)) {
        const key = previewKey(raw);
        if (!key || this.#layers.has(key) || !reachable.has(key)) continue;
        this.#layers.set(key, layer);
        claimed += 1;
      }

      if (claimed === 0) layer.remove();
      else if (!first) first = layer;
    }

    this.#resting = this.#layers.get(triggerKey) ?? first;

    return this.#layers.size > 0;
  }

  /**
   * Pointer or keyboard focus landing on a link inside the panel. Both events are handled
   * identically, which gives the rail keyboard parity for free: tabbing through the panel
   * walks the preview exactly as hovering does.
   * @param {Event} event
   */
  #onIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    if (!link || !this.#grid?.contains(link)) return;

    this.#ensureAdopted().then((ready) => {
      if (!ready) return;

      const layer = this.#layers.get(previewKey(link.getAttribute('href')));
      if (!layer) return;

      this.#load(layer, 'high');
      this.#scheduleBackfill();
      this.#show(layer);
    });
  };

  /* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§4 */
  #onEnter = () => {
    this.#ensureAdopted().then((ready) => {
      if (!ready) return;

      this.#load(this.#resting, 'high');
      this.#show(this.#resting);
      this.#scheduleBackfill();
    });
  };

  /* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§5 */
  #onLeave = () => {
    this.#cancelBackfill();
  };

  /** @param {HTMLElement | null} layer */
  #show(layer) {
    if (!layer || layer === this.#active) return;

    this.#active?.removeAttribute('data-r-preview-active');
    layer.setAttribute('data-r-preview-active', '');
    this.#active = layer;
  }

  /**
   * Clone one layer's parked <template> into its frame, which is what actually starts the
   * image request. Idempotent: the template is removed as it is used, so a layer already
   * loaded is a no-op.
   *
   * The markup ships inert because `loading="lazy"` does not defer these: the panel is
   * `visibility: hidden`, which still lays out, and those boxes are inside the viewport —
   * so without the template every image is fetched on every page load, for a menu most
   * visitors never open. See snippets/r-menu-preview-layer.liquid.
   *
   * @param {HTMLElement | null} layer
   * @param {'high' | 'low'} priority
   * @returns {boolean} whether this call started a load
   */
  #load(layer, priority) {
    const template = layer?.querySelector('template.r-menu-preview__source');
    if (!(template instanceof HTMLTemplateElement)) return false;

    const frame = template.parentElement;
    template.remove();
    frame?.append(template.content.cloneNode(true));

    const image = frame?.querySelector('img');
    if (image instanceof HTMLImageElement) {
      // Already in the DOM and wanted, so lazy would only add an extra viewport check.
      image.loading = 'eager';
      // Backfilled images must not compete with anything the visitor is actually looking
      // at — including the page behind the menu.
      image.fetchPriority = priority;
    }

    return true;
  }

  /* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§6 */
  #scheduleBackfill() {
    if (this.#backfilling) return;
    this.#backfilling = true;

    const step = () => {
      if (!this.#backfilling || !this.isConnected) return;

      const template = this.querySelector('template.r-menu-preview__source');
      if (!template) {
        this.#backfilling = false;
        return;
      }

      this.#load(template.closest('.r-menu-preview__layer'), 'low');
      requestIdleCallback(step, { timeout: 2000 });
    };

    requestIdleCallback(step, { timeout: 2000 });
  }

  #cancelBackfill() {
    this.#backfilling = false;
  }
}

if (!customElements.get('r-menu-preview')) {
  customElements.define('r-menu-preview', MenuPreview);
}
